import React, { useContext, useMemo } from 'react';
import '../styles/Cart.css';
import BsImages from '../icons/BsImages';
import AiOutlinePlus from '../icons/AiOutlinePlus';
import AiOutlineMinus from '../icons/AiOutlineMinus';
import { CartContext } from './context/CartContext';
import { Link } from 'react-router-dom';
import getSymbolFromCurrency from 'currency-symbol-map';
import { bool, number, oneOfType, string } from 'prop-types';
import ResponsiveImage from './ResponsiveImage';
import AppContext from './context/AppContext';
import constants from '../constants.json' assert { type: 'json' };
import useDynamicPath from '../hooks/useDynamicPath';

export default function CartItem({
    isMobile,
    lineItemId,
    inventoryLevelId,
    price,
    quantity,
    sku,
    title,
    availableQuantity,
    availableForSale,
    imageUrl,
    handle,
    bypassNegativeQuantity,
}) {
    const { appContext } = useContext(AppContext);
    const productPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.PRODUCT_PATH });
    const appConfig = appContext.config.themeConfig;
    const {
        cartState,
        checkoutData,
        cartUpdating,
        addLineItem,
        setLineItem,
        delLineItem,
        reloadLineItems,
        fetchCheckoutData,
    } = useContext(CartContext);
    // Add hints that the inventory level is exhausted
    // https://app.clickup.com/t/86a1dkgna
    const quantityToAdd = useMemo(() => parseInt((bypassNegativeQuantity ? Infinity : availableQuantity) - quantity),
        [bypassNegativeQuantity, availableQuantity, quantity]);
    return (<div
        className={'cart-item'}>
        {imageUrl && (<div
            className={'cart-item--image'}><ResponsiveImage
                imageUrl={imageUrl}
                imageAlt={title || ''}
                mobileSize={'75px'}
                desktopSize={'75px'}></ResponsiveImage></div>)}
        {!imageUrl && (<div
            className={'cart-item--image'}>
            <BsImages
                style={{ maxWidth: '150px' }} />
        </div>)}
        <div
            className={'cart-item--description'}>
            <Link
                to={`${productPath[0].pathBeforeParams}/${handle}`}
                className={'cart-item--title'}>{title}</Link>
            <p
                className={'cart-item--sku-text'}>SKU: {sku}</p>
            <p
                className={'cart-item--qty'}>Qty: {quantity}</p>
            <span
                className={'cart-item--price'}>{getSymbolFromCurrency(appConfig.storeCurrency)}{(price).toFixed(2)} {appConfig.storeCurrency}</span>
        </div>
        <div
            className={'cart-item--actions'}>
            <button
                style={{ ...(quantityToAdd <= 0 || cartUpdating) && ({ color: 'lightgray' }) }}
                aria-label={'Add item'}
                onClick={() => addLineItem(inventoryLevelId, 1)}
                disabled={(quantityToAdd <= 0 || cartUpdating)}>
                <AiOutlinePlus
                    size={!isMobile ? '25px' : '100%'} />
            </button>
            <button
                style={{ ...(quantity - 1 <= 0 || cartUpdating) && ({ color: 'lightgray' }) }}
                aria-label={'Reduce item quantity'}
                onClick={() => setLineItem(inventoryLevelId, quantity - 1)}
                disabled={(quantity - 1 <= 0 || cartUpdating)}>
                <AiOutlineMinus
                    size={!isMobile ? '25px' : '100%'} />
            </button>
            <button
                style={{ ...cartUpdating && ({ color: 'lightgray' }) }}
                aria-label={'Remove item'}
                onClick={() => delLineItem(lineItemId)}
                disabled={cartUpdating}>
                Remove
            </button>
        </div>
    </div>);
}

CartItem.propTypes = {
    isMobile: bool,
    lineItemId: number,
    inventoryLevelId: number,
    price: oneOfType([number, string]),
    quantity: oneOfType([number, string]),
    sku: string,
    title: string,
    availableQuantity: oneOfType([number, string]),
    availableForSale: oneOfType([number, bool]),
    imageUrl: string,
    handle: string,
    bypassNegativeQuantity: bool,
};